<template>
  <ClientOnly>
    <div>
      <VacancyContainer
        v-if="!$isServer && !canRecordInterview"
        :id="vacancyId"
        v-slot="{ company }"
        @ready="handleVacancyContainerReady"
      >
        <VRow
          justify="center"
        >
          <VCol cols="12" md="8" xl="7">
            <CompanySmallCard v-if="$vuetify.breakpoint.smAndDown" :company="company" />
            <AppIosMessage v-if="isIOS" :candidate="candidate" />
            <VCard v-else class="mt-6">
              <CanNotRecordInterview
                :cancel-text="$t('back')"
                @cancel="$router.back()"
              >
                <template #description>
                  <VRow justify="center">
                    <VCol cols="12" md="8">
                      <div class="text-center">
                        {{ $t('browserListTip') }}:
                      </div>
                    </VCol>
                  </VRow>
                </template>
                <template #buttons>
                  <VRow justify="center">
                    <VCol cols="12 d-flex flex-column align-center">
                      <VDivider width="90%" class="mb-4" />
                      <VBtn color="success" class="px-4 my-6 center" @click="$router.back()">
                        {{ $t('back') }}
                      </VBtn>
                    </VCol>
                  </VRow>
                </template>
              </CanNotRecordInterview>
            </VCard>
          </VCol>
        </VRow>
      </VacancyContainer>
      <VacancyContainer v-else-if="canView" :id="vacancyId" v-slot="{ company }">
        <VStepper
          v-if="!isDone && !isInterrupted"
          v-model="step"
          alt-labels
          class="transparent elevation-0"
        >
          <VRow justify="center">
            <VCol cols="12" md="8" xl="6">
              <VRow justify="center">
                <VCol cols="12" lg="10">
                  <VStepperHeader class="elevation-0 caption text-center stepper-header">
                    <VStepperStep step="1">
                      {{ $t('cameraOn') }}
                    </VStepperStep>
                    <VDivider />
                    <VStepperStep step="2">
                      {{ $t('micSettings') }}
                    </VStepperStep>
                    <VDivider />
                    <VStepperStep step="3">
                      {{ $t('cameraSettings') }}
                    </VStepperStep>
                    <VDivider />
                    <VStepperStep step="4">
                      {{ $t('interview') }}
                    </VStepperStep>
                  </VStepperHeader>
                </VCol>
              </VRow>
            </VCol>
          </VRow>
          <VRow justify="center">
            <VCol
              cols="12"
              :md="(step === 4) ? 12 : 8"
              :xl="(step === 4) ? 12 : 6"
            >
              <VCard
                :elevation="(step === 4) ? 0 : 1"
                :color="(step === 4) ? 'transparent' : '#ffffff'"
                class="pa-3 pa-md-5 px-md-12 mb-5"
              >
                <VStepperItems class="full-screen-item mt-8">
                  <VStepperContent :step="1" class="pa-0">
                    <InterviewRequestPermissions v-if="step === 1" @done="step = 2" />
                  </VStepperContent>
                  <VStepperContent :step="2" class="pa-0">
                    <InterviewMicrophoneCheck
                      v-if="step === 2"
                      :candidate="candidate"
                      @done="step = 3"
                    />
                  </VStepperContent>
                  <VStepperContent :step="3" class="pa-0">
                    <InterviewCameraCheck
                      v-if="step === 3"
                      @done="step = 4"
                    />
                  </VStepperContent>
                  <VStepperContent :step="4" class="pa-0">
                    <VRow justify="center">
                      <VCol cols="12" lg="10" xl="8" class="relative">
                        <ClientOnly>
                          <h1 class="d-none headline text-center mb-6">
                            {{ $t('interviewRecord') }} <i class="dot" />
                          </h1>
                          <div v-if="company.logo" class="interview-avatar">
                            <CompanyAvatar :company="company" :size="50" />
                          </div>
                          <InterviewRecorder
                            v-if="step === 4"
                            :candidate="candidate"
                            :interviewer="vacancy.script.interviewer"
                            :questions="vacancy.script.questions"
                            :resume="true"
                            :vacancy-id="vacancyId"
                            @stopped="stopped"
                            @interrupted="interrupted"
                          />
                          <Portal to="support">
                            <SupportTicketDialog v-if="$route.meta.supportBtn"
                                                 :candidate="candidate"
                            />
                          </Portal>
                        </ClientOnly>
                      </VCol>
                    </VRow>
                  </VStepperContent>
                </VStepperItems>
              </VCard>
            </VCol>
          </VRow>
        </VStepper>
        <InterviewDone v-else-if="isDone" :company="company" :vacancy="vacancy" />
        <InterviewInterrupted v-else-if="isInterrupted" />
      </VacancyContainer>
    </div>
  </ClientOnly>
</template>

<script>
import { ref } from '@vue/composition-api';
import { mapGetters } from 'vuex';
import ClientOnly from 'vue-client-only';
import CanNotRecordInterview
  from '@huntica/huntica-interview-module/src/components/CanNotRecordInterview.vue';
import { canRecordInterview } from '@huntica/huntica-interview-module/src/helpers';
import {
  hasSelectedMicrophone,
  hasSelectedCamera,
} from '@huntica/huntica-interview-module/src/mediaHelpers';
import { initLogRocket } from '@/logrocket';
import AppIosMessage from '@/components/AppIosMessage.vue';
import useLocale from '@/compositions/useLocale';
import { isIOS } from '@/functions';
import VacancyContainer from '../components/Vacancy/VacancyContainer.vue';
import CompanySmallCard from '../components/Company/CompanySmallCard.vue';
import CompanyAvatar from '../components/Company/CompanyAvatar.vue';
import InterviewDone from '../components/Interview/InterviewDone.vue';
import InterviewInterrupted from '../components/Interview/InterviewInterrupted.vue';
import SupportTicketDialog from '../components/SupportTicketDialog.vue';
import { candidateStatuses } from '../enums';

export default {
  name: 'InterviewTestContinue',
  components: {
    AppIosMessage,
    CanNotRecordInterview,
    ClientOnly,
    InterviewDone,
    InterviewInterrupted,
    VacancyContainer,
    CompanySmallCard,
    CompanyAvatar,
    InterviewRequestPermissions: process.browser
      ? () => import('../components/Interview/InterviewRequestPermissions.vue') : null,
    InterviewMicrophoneCheck: process.browser
      ? () => import('../components/Interview/InterviewMicrophoneCheck.vue') : null,
    InterviewCameraCheck: process.browser
      ? () => import('../components/Interview/InterviewCameraCheck.vue') : null,
    SupportTicketDialog,
    InterviewRecorder: process.browser
      ? () => import('../components/Interview/InterviewRecorder.vue')
      : null,
  },
  metaInfo() {
    return {
      title: this.isDone ? this.$t('isDone') : this.$t('continue'),
    };
  },
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      canRecordInterview: process.browser && canRecordInterview(),
      step: 1,
      isDone: false,
      isInterrupted: false,
    };
  },
  setup() {
    const { changeLocale, getPreferredLocale, saveLocaleIfEmptyStorageValue } = useLocale();

    function handleVacancyContainerReady({ vacancy }) {
      const locale = getPreferredLocale() ?? vacancy.script.interviewer.locale;
      changeLocale(locale);
      saveLocaleIfEmptyStorageValue(locale);
    }

    return {
      changeLocale,
      canRecordInterview: process.browser && canRecordInterview(),
      step: ref(2),
      isDone: ref(false),
      isInterrupted: ref(false),
      handleVacancyContainerReady,
    };
  },
  computed: {
    ...mapGetters('candidate', ['candidate', 'vacancy']),
    canView() {
      return this.candidate !== null
        && this.vacancy.id === this.vacancyId;
    },
  },
  watch: {
    async step() {
      if (this.step === 2) {
        if (await hasSelectedMicrophone() && await hasSelectedCamera()) {
          this.step = 4;
        } else if (await hasSelectedMicrophone()) {
          this.step = 3;
        }
      }
    },
  },
  beforeCreate() {
    initLogRocket(this.$route.query.disableLogRocket);
  },
  async created() {
    await this.$store.dispatch('candidate/fetchData', { vacancyId: this.vacancyId });
    if (!canRecordInterview) {
      return null;
    }
    if (!this.candidate || this.vacancy.id !== this.vacancyId) {
      return this.$router.push({
        name: 'vacancy',
        params: {
          vacancyId: this.vacancyId,
        },
      });
    }

    if (this.candidate.status === candidateStatuses.PASSED_DEMO_INTERVIEW) {
      this.isDone = true;
    }

    if (this.candidate.status !== candidateStatuses.INVITED
      && this.candidate.status !== candidateStatuses.PASSED_DEMO_INTERVIEW
      && this.candidate.status !== candidateStatuses.IMPORTED
    ) {
      return this.$router.push({
        name: 'interviewResult',
        params: {
          companyId: this.vacancy.companyId,
          vacancyId: this.vacancy.id,
        },
      });
    }

    if (!this.candidate.interview) {
      return this.$router.push({
        name: 'vacancyReply',
        params: {
          companyId: this.vacancy.companyId,
          vacancyId: this.vacancy.id,
        },
      });
    }

    return null;
  },
  methods: {
    stopped() {
      this.isDone = true;
    },
    interrupted() {
      this.isInterrupted = true;
    },
    isIOS,
  },
};
</script>
<style lang='scss' scoped>
.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
  background: red;
  position: relative;
  top: -10px;
}

.interview-avatar {
  width: 60px;
  height: 60px;
  margin: 5px;
  position: absolute;
  left: 15px;
  top: 15px;
  z-index: 2;
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .interview-avatar {
    left: 30px;
    top: 30px;
  }
}

.stepper-header {
  &::v-deep {
    .v-stepper__label {
      text-align: center;
    }

    .v-divider {
      border-style: dotted;
      margin-top: 37px;
    }

    .v-stepper__step {
      flex-basis: 130px;

      .v-stepper__step__step {
        background: #fff !important;
        color: map-get($grey, 'lighten-1');
        font-size: 14px !important;
      }

      &.v-stepper__step--active {
        .v-stepper__step__step {
          background: map-get($blue, 'base') !important;
          color: #fff;
        }
      }
    }
  }
}
</style>

<i18n locale='en' lang='yaml'>
browserListTip: "To take the video interview, you should use the latest version of one of the following browsers:"
back: Back
profile: personal data
cameraOn: permissions
micSettings: mic setup
cameraSettings: camera setup
interview: interview
interviewRecord: Record video interview
continue: Continue the video interview
isDone: Video interview completed
email: email
</i18n>

<i18n locale='ru' lang='yaml'>
browserListTip: >-
  Для прохождения видео-интервью необходимо воспользоваться последней
  версией одного из следующих браузеров
back: Назад
profile: профиль
cameraOn: включите камеру
micSettings: настройка микрофона
cameraSettings: настройка камеры
interview: интервью
interviewRecord: Запись видео-интервью
continue: Продолжить видео-интервью
isDone: Видео-интервью завершено
email: почта
</i18n>
